import { ref, watch } from 'vue'
import { useStore } from 'vuex'

/*
 * Opening Crisp starts a real conversation with a human on the support team —
 * it is not a self-serve help widget. So every entry point has to confirm
 * first, and set the expectation that a reply can take up to 24 hours rather
 * than arriving instantly.
 *
 * The confirmation state lives at MODULE scope, not inside the composable, so
 * the single dialog rendered in App.vue and every `openCrispChat()` caller
 * anywhere in the tree share it. That also means the warning cannot be skipped
 * by a future call site: `openCrispChat()` only ever *requests* a chat, and the
 * dialog is the one path that actually opens one.
 */

const isConfirmOpen = ref(false)
const pendingMessage = ref(null)
/* Whether the dialog offers an instant answer from the tutorial first (App.vue,
   TutorialQuickAsk.vue). Off for chats that are already about something
   specific — a report on a campaign, a staff notice, a beta request — where
   "try the AI first" would only be in the way. */
const offerTutorial = ref(true)

export const CRISP_REPLY_SLA_HOURS = 24

/* Set just before the panel itself opens Crisp, so the bubble gate below lets
   that one through. */
let openingFromPanel = false
let gateInstalled = false

/* Unread support replies, for the panel's own help button (SupportChatButton.vue),
   which stands in for Crisp's hidden bubble while signed in. */
export const crispUnread = ref(0)
let launcherInstalled = false
let launcherActive = () => false

const crispIs = (key) => {
  try {
    return window.$crisp.is(key)
  } catch {
    return null // Crisp not loaded yet: `$crisp` is still index.html's queue.
  }
}

const refreshUnread = () => {
  try {
    crispUnread.value = Number(window.$crisp.get('chat:unread:count')) || 0
  } catch {
    /* Not loaded yet. */
  }
}

/* While signed in, Crisp's own bubble stays hidden except while its chat is
   open; the panel's help button replaces it. Signed out, Crisp is untouched. */
const applyCrispVisibility = () => {
  if (!window.$crisp) return
  if (!launcherActive()) {
    window.$crisp.push(['do', 'chat:show'])
    return
  }
  if (crispIs('chat:opened') !== true) window.$crisp.push(['do', 'chat:hide'])
}

/**
 * Replaces Crisp's bubble with the panel's help button for signed-in
 * customers, so a new conversation always starts from the dialog (and its
 * instant answer from the tutorial) — without Crisp flashing open first, which
 * the bubble gate below cannot avoid.
 *
 * Crisp is hidden from the start (the command is queued before its script
 * runs), shown only while its chat is open, and hidden again when it closes.
 * Unread replies are counted onto the help button, since the bubble that
 * would have shown them is hidden. Install once, from App.vue.
 */
export function installCrispLauncher(isActive) {
  if (launcherInstalled || !window.$crisp) return
  launcherInstalled = true
  launcherActive = isActive
  applyCrispVisibility()
  window.$crisp.push(['on', 'session:loaded', () => {
    applyCrispVisibility()
    refreshUnread()
  }])
  window.$crisp.push(['on', 'chat:closed', () => {
    applyCrispVisibility()
    refreshUnread()
  }])
  window.$crisp.push(['on', 'chat:opened', () => {
    crispUnread.value = 0
  }])
  window.$crisp.push(['on', 'message:received', refreshUnread])
  watch(isActive, applyCrispVisibility)
}

/**
 * Puts the same dialog in front of Crisp's own chat bubble.
 *
 * The bubble (bottom-right, from index.html) opens Crisp directly, so a new
 * conversation started there skipped the dialog — and with it the instant
 * answer from the tutorial. When Crisp reports the chat opening and this
 * visitor has no conversation yet (`session:ongoing` is false), the chat is
 * closed again and the dialog shown instead; "Start chat" there opens Crisp
 * for real. Anyone with a conversation going is never stopped: they may be
 * coming back for a reply.
 *
 * `shouldGate()` is checked on every open — the panel passes "signed in",
 * because the instant answer needs the session. Install once, from App.vue.
 */
export function installCrispBubbleGate(shouldGate) {
  if (gateInstalled || !window.$crisp) return
  gateInstalled = true
  window.$crisp.push(['on', 'chat:opened', () => {
    if (openingFromPanel) {
      openingFromPanel = false
      return
    }
    if (!shouldGate()) return
    let ongoing = true
    try {
      ongoing = window.$crisp.is('session:ongoing')
    } catch {
      /* Unknown state: let Crisp open rather than risk hiding a reply. */
    }
    if (ongoing) return
    window.$crisp.push(['do', 'chat:close'])
    pendingMessage.value = null
    offerTutorial.value = true
    isConfirmOpen.value = true
  }])
}

export function useCrispChat() {
  const store = useStore()

  // The real open. Private on purpose — only the confirmation dialog calls it.
  const startChat = () => {
    if (!window.$crisp) return

    const email = store?.state?.auth?.user?.user?.email
    const userId = store?.state?.auth?.user?.user?.id
    if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      window.$crisp.push(['set', 'user:email', [email]])
    }
    if (userId) {
      window.$crisp.push(['set', 'session:data', [[['user_id', String(userId)]]]])
    }

    openingFromPanel = true
    window.$crisp.push(['do', 'chat:show'])
    window.$crisp.push(['do', 'chat:open'])
    if (pendingMessage.value) {
      window.$crisp.push(['do', 'message:send', ['text', pendingMessage.value]])
    }
    pendingMessage.value = null
  }

  /**
   * Ask to start a support chat. Shows the confirmation dialog rather than
   * opening Crisp directly.
   *
   * Keeps the original early return: when the Crisp script hasn't loaded there
   * is nothing to open, so warning about a chat that cannot start would be
   * worse than the existing silent no-op.
   *
   * Returns whether a chat was actually requested. Entry points that are only a
   * "chat to us" affordance can keep ignoring it — but a call site that is
   * HANDING OVER something the customer typed (the broken-job report) has to
   * know, or a blocked script silently swallows the report.
   *
   * @returns {boolean}
   */
  const openCrispChat = (message, { offerTutorial: offer = true } = {}) => {
    if (!window.$crisp) return false
    pendingMessage.value = message ?? null
    offerTutorial.value = offer
    isConfirmOpen.value = true
    return true
  }

  /** Replace the message the chat will open with (the dialog's quick ask
      hands over the question the customer already typed). */
  const setPendingMessage = (message) => {
    pendingMessage.value = message
  }

  /**
   * The help button: a customer with a conversation going (or unread replies)
   * goes straight back to it; anyone else gets the dialog, instant answer first.
   */
  const openSupport = () => {
    if (!window.$crisp) return
    if (crispIs('session:ongoing') === true || crispUnread.value > 0) {
      pendingMessage.value = null
      startChat()
      return
    }
    openCrispChat()
  }

  const confirmCrispChat = () => {
    isConfirmOpen.value = false
    startChat()
  }

  const cancelCrispChat = () => {
    isConfirmOpen.value = false
    pendingMessage.value = null
  }

  return {
    openCrispChat,
    confirmCrispChat,
    cancelCrispChat,
    isConfirmOpen,
    pendingMessage,
    offerTutorial,
    setPendingMessage,
    openSupport,
    crispUnread
  }
}
